import { Immutable } from "immer";
import React from "react";

// A structural view of a reactivated-serialized Django form widget. We type
// against this rather than the per-package `_Types["globals"]["Widget"]` union
// because that union only contains the widgets a given package's forms actually
// reference -- a shared renderer must accept any widget tag without narrowing to
// `never`. Every reactivated widget carries these fields (see
// reactivated/serialization/widgets.py); `type` / `optgroups` are widget-specific.
type OptgroupMember = Immutable<{
    value: string | number | boolean | null;
    label: string;
}>;
type Optgroup = readonly [unknown, ReadonlyArray<OptgroupMember>, unknown];

export type SerializedWidget = Immutable<{
    tag: string;
    name: string;
    required: boolean;
    is_hidden: boolean;
    value: string | ReadonlyArray<string> | null;
    type?: string;
    optgroups?: ReadonlyArray<Optgroup>;
    attrs: {
        id: string;
        maxlength?: string;
        cols?: string;
        rows?: string;
        checked?: true;
        multiple?: string;
    };
}>;

const asValue = (value: SerializedWidget["value"]): string =>
    typeof value === "string" ? value : "";

const asNumber = (value: string | undefined): number | undefined =>
    value == null ? undefined : Number(value);

/**
 * Renders the bare HTML control for a single reactivated-serialized Django form
 * widget (a reactivated FieldHandler's `field.widget`) -- `<input>`,
 * `<textarea>`, or `<select>`. The surrounding
 * `form__field` wrapper, `<label>`, and error list are the caller's
 * responsibility (they differ per site/page).
 *
 * Reactivated only serializes a fixed set of widget attrs (id / required /
 * maxlength / cols / rows / checked / multiple). Callers can supply essential
 * page-level accessibility and autofill attributes that are not in that schema.
 */
export const ReactivatedFormWidget = (
    props: Immutable<{
        widget: SerializedWidget;
        ariaDescribedBy?: string;
        ariaInvalid?: boolean;
        autoComplete?: string;
        // Select-only: callers that need to react to a selection (e.g.
        // driving other on-page copy from the chosen option) can supply this;
        // the widget stays otherwise uncontrolled (defaultValue/defaultChecked).
        onChange?: React.ChangeEventHandler<HTMLSelectElement>;
    }>,
): React.JSX.Element => {
    const widget = props.widget;
    const id = widget.attrs.id;
    const required = widget.required || undefined;
    // Shared across every widget element; React omits undefined values from
    // the DOM whether these are inline or spread.
    const a11yAttrs = {
        "aria-describedby": props.ariaDescribedBy,
        "aria-invalid": props.ariaInvalid || undefined,
        "autoComplete": props.autoComplete,
    };

    switch (widget.tag) {
        case "django.forms.widgets.Select":
        case "django.forms.widgets.SelectMultiple": {
            const multiple = widget.attrs.multiple ? true : undefined;
            return (
                <select
                    id={id}
                    name={widget.name}
                    required={required}
                    multiple={multiple}
                    onChange={props.onChange}
                    defaultValue={widget.value ?? undefined}
                    {...a11yAttrs}
                >
                    {(widget.optgroups ?? []).flatMap((group) =>
                        group[1].map((opt) => (
                            <option
                                key={String(opt.value)}
                                value={
                                    typeof opt.value === "string"
                                        ? opt.value
                                        : String(opt.value ?? "")
                                }
                            >
                                {opt.label}
                            </option>
                        )),
                    )}
                </select>
            );
        }
        case "django.forms.widgets.Textarea":
            return (
                <textarea
                    id={id}
                    name={widget.name}
                    required={required}
                    cols={asNumber(widget.attrs.cols)}
                    rows={asNumber(widget.attrs.rows)}
                    maxLength={asNumber(widget.attrs.maxlength)}
                    defaultValue={asValue(widget.value)}
                    {...a11yAttrs}
                />
            );
        case "django.forms.widgets.CheckboxInput":
            return (
                <input
                    type="checkbox"
                    id={id}
                    name={widget.name}
                    required={required}
                    defaultChecked={widget.attrs.checked ? true : undefined}
                    {...a11yAttrs}
                />
            );
        case "django.forms.widgets.HiddenInput":
            return (
                <input
                    type="hidden"
                    id={id}
                    name={widget.name}
                    defaultValue={asValue(widget.value)}
                    {...a11yAttrs}
                />
            );
        default:
            // Text-like inputs: TextInput / EmailInput / URLInput / NumberInput /
            // PasswordInput / DateInput. `type` carries the actual input type
            // (e.g. "tel" for a phone field), so render it verbatim.
            return (
                <input
                    type={widget.type ?? "text"}
                    id={id}
                    name={widget.name}
                    required={required}
                    maxLength={asNumber(widget.attrs.maxlength)}
                    defaultValue={asValue(widget.value)}
                    {...a11yAttrs}
                />
            );
    }
};
